// PATH: SaberpDeals/frontend/src/auth/AuthContext.tsx
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, getToken, setToken, clearToken } from '@/lib/api';

interface AuthState {
    ready: boolean;          // finished the initial token check
    authed: boolean;
    username: string | null;
    login: (username: string, password: string) => Promise<void>;
    logout: () => void;
}

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [ready, setReady] = useState(false);
    const [authed, setAuthed] = useState(false);
    const [username, setUsername] = useState<string | null>(null);

    // On load, if we have a stored token, verify it against /me.
    useEffect(() => {
        (async () => {
            if (!getToken()) { setReady(true); return; }
            try {
                const { data } = await api.get('/api/deals/auth/me');
                setAuthed(true);
                setUsername(data.username);
            } catch {
                clearToken();
            } finally {
                setReady(true);
            }
        })();
    }, []);

    async function login(u: string, p: string) {
        const { data } = await api.post('/api/deals/auth/login', { username: u, password: p });
        setToken(data.token);
        setAuthed(true);
        setUsername(data.username);
    }

    function logout() {
        clearToken();
        setAuthed(false);
        setUsername(null);
        window.location.href = '/login';
    }

    return (
        <AuthCtx.Provider value={{ ready, authed, username, login, logout }}>
            {children}
        </AuthCtx.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthCtx);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
}
